import { defineStore } from 'pinia'
import { onBeforeMount, reactive, ref, watch } from 'vue'
import UserService from '@/services/UserService'
import { localDriver } from '@/utils/localDriver'

export const useUserStore = defineStore('user', () => {
  //console.log('pinia activated')
  const initializing = ref(true)
  const serverErrors = ref([])
  const verifyCode = ref(null)
  const showLoginDlg = ref('')
  // One-shot coupon granted at registration, surfaced once by checkState for the success banner.
  const registerCoupon = ref(null)
  const initUserState = {
    hasCompany: null,
    customerGroupId: null,
    avatar: null,
    customerId: null,
    isLogin: false,
    customerName: null,
    email: null,
    firstName: null,
    lastName: null,
    isMember: null,
    memberType: null,
    memberTypeName: null,
    accountId: null
  }

  const user = reactive<{
    hasCompany: boolean
    customerGroupId: Number
    avatar: string
    customerId: number
    isLogin: boolean
    customerName: string
    email: string
    firstName: string
    lastName: string
    isMember: boolean
    memberType: number
    memberTypeName: string
    accountId: number
    mmshipHistory: Object
  }>(JSON.parse(JSON.stringify(initUserState)))

  /**
   * Check user login status
   */
  async function checkState() {
    try {
      const result = await UserService.checkState()
      let avatar: string = result.avater || '/images/icon_user_b.png'
      if (avatar.startsWith('..')) {
        avatar = avatar.replace('..', '')
      }
      user.avatar = avatar
      user.customerName = result.customer_name
      user.email = result.email
      user.customerGroupId = result.customer_group_id
      user.customerId = result.customer_id
      user.hasCompany = result.has_company
      user.isLogin = result.is_login
      user.firstName = result.first_name
      user.lastName = result.last_name
      user.isMember = result.is_member
      user.memberType = result.member_type
      user.memberTypeName = result.member_type_name
      user.accountId = result.accountId
      user.mmshipHistory = result.mmshipHistory
      // Register coupon is returned only once (right after signup); keep it for the success banner.
      if (result.register_coupon) {
        registerCoupon.value = result.register_coupon
      }
      await localDriver.setItem('customer_id', result.customer_id || 0)
      await localDriver.setItem('customerInfo', result)
    } catch (error) {
      console.warn('[UserStore.checkState] failed:', error)
    }
  }

  async function keepSession() {
    checkState().catch(() => { })
  }
  let resolveInit = null;
  async function waitInit() {
    await new Promise<void>((resolve, reject) => {
      resolveInit = resolve
      if (false == initializing.value) {
        resolveInit()
      }
    })
  }

  //check session every 5 min
  setInterval(keepSession, 5000 * 60 * 1000)

  checkState().catch(() => { }).finally(() => {
    initializing.value = false
    if (null != resolveInit) {
      resolveInit()
    }
  })

  async function login(data: {
    email: string
    password: string
    remember?: boolean
    rem?: number
    groupId?: number
  }) {
    const params = { ...data }
    if (data.rem == null) {
      params.rem = 0
    }
    if (data.remember == null) {
      params.remember = false
    }
    const result = await UserService.login(params)
    const errors = []
    for (const field in result) {
      if (field.startsWith('error_') && result[field] != '') {
        //console.log('----------------err--------------', [field, result])
        errors.push(result[field])
        break
      }
    }
    serverErrors.value = errors
    if (errors.length == 0) {
      await checkState()
      return true
    } else {
      return false
    }
  }

  async function sendVerifyCode(data: { firstname: string; phoneNumber: string }) {
    const result = await UserService.sendVerifyCode(data.firstname, data.phoneNumber)
    const errors = []
    if (!result.success) {
      errors.push(result.msg)
    } else {
      verifyCode.value = result.data
    }
    serverErrors.value = errors
    return result.success
  }

  async function saveAgentInfo(data: {
    customer_company_id?: number
    customer_company_join_password?: string
    company_name?: string
    company_telpre?: string
    company_phone?: string
    company_email?: string
    company_web_site?: string
    company_address?: string
    company_license_type?: string
    company_license_number?: string
  }) {
    return UserService.saveAgentInfo(data)
  }

  async function register(data: {
    agreeSmsTerms?: boolean
    agree: boolean
    telpre: string
    telephone: string
    firstname: string
    lastname: string
    email: string
    smsCode: string
    password: string
    confirm: string
    customer_group_id?: number
    ref_accountid?: string
  }) {
    const result = await UserService.register(data)
    const errors = []
    for (const field in result) {
      if (field.startsWith('error_') && result[field] != '') {
        errors.push(result[field])
        break
      }
    }
    serverErrors.value = errors
    if (errors.length == 0) {
      await checkState()
      return true
    } else {
      return false
    }
  }

  async function logout(params: {
    requestLogin: boolean
  } = {
      requestLogin: false
    }) {
    await UserService.logout()
    // Object.assign(user, JSON.parse(JSON.stringify(initUserState)))
    Object.assign(user, initUserState)
    if (!params.requestLogin) {
      const userPath = '/user/'
      if (window.location.pathname.includes(userPath)) {
        window.location.href = import.meta.env.BASE_URL
      }
      else {
        window.location.reload()
      }
    }
    else {
      showLoginDlg.value = 'login'
    }
  }

  window.addEventListener('auth:expired', async () => {
    setTimeout(async () => {
      await logout({ requestLogin: true })
    }, (5000));
  })

  let onLoginDlgClosedCallback: (loginResult: boolean) => boolean | void = null

  async function closeLoginRequest() {
    if (onLoginDlgClosedCallback) {
      const keep = onLoginDlgClosedCallback(user.isLogin) || false
      if (!keep) {
        onLoginDlgClosedCallback = null
      }
    }
    showLoginDlg.value = ''
  }

  async function requestLogin(callback?: (loginResult: boolean) => boolean | void) {
    onLoginDlgClosedCallback = callback
    showLoginDlg.value = 'login'
  }

  async function requestRegister(callback?: (loginResult: boolean) => boolean | void) {
    onLoginDlgClosedCallback = callback
    showLoginDlg.value = 'register'
  }

  return {
    user,
    //是否正在初始化页面
    initializing,
    //上一次操作在服务端检测到的错误
    serverErrors,
    //验证码
    verifyCode,
    //是否显示login对话框
    showLoginDlg,
    //注册时发放的优惠券(一次性,用于注册成功横幅)
    registerCoupon,
    //等待用户数据第一次加载完毕
    waitInit,
    login,
    //检查登录状态
    checkState,
    logout,
    register,
    saveAgentInfo,
    sendVerifyCode,
    requestLogin,
    requestRegister,
    closeLoginRequest
  }
})
